/**
 * The signed-in session: access token, refresh token and user, in one place.
 *
 * The backend (SimpleJWT) issues 30-minute access tokens and rotating 90-day
 * refresh tokens in the response body — there is no HttpOnly-cookie session
 * to lean on — so both live in localStorage, which is also what keeps
 * several tabs on the same session.
 *
 * Every sign-in and sign-out bumps a generation number. Work that started in
 * one session (a request, a refresh) carries its generation, and anything it
 * finishes after the session changed is dropped: an old request can never
 * sign out, or overwrite the tokens of, a newer session.
 */

const ACCESS_KEY = 'token'
const REFRESH_KEY = 'refresh_token'
const USER_KEY = 'user'

let generation = 0
const listeners = new Set()

const read = key => {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}
const write = (key, value) => {
  try {
    if (value === null || value === undefined) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    /* storage unavailable (private mode): the in-memory state still works for this page */
  }
}

const emit = (type, detail) => {
  for (const fn of listeners) {
    try {
      fn(type, detail)
    } catch {
      /* a listener must not break the others */
    }
  }
}

export const getAccessToken = () => read(ACCESS_KEY)
export const getRefreshToken = () => read(REFRESH_KEY)

export const getStoredUser = () => {
  const raw = read(USER_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export const currentGeneration = () => generation
export const isCurrentGeneration = g => g === generation

/** A new sign-in: replaces whatever session was there. */
export function startSession({ access, refresh, user }) {
  if (!access) throw new Error('startSession needs an access token')
  generation += 1
  write(ACCESS_KEY, access)
  write(REFRESH_KEY, refresh || null)
  if (user) write(USER_KEY, JSON.stringify(user))
  emit('start', { user })
}

/** Rotated tokens for the session that asked — ignored if the session changed meanwhile. */
export function storeRefreshedTokens({ access, refresh }, forGeneration) {
  if (forGeneration !== undefined && forGeneration !== generation) return false
  if (!access) return false
  write(ACCESS_KEY, access)
  if (refresh) write(REFRESH_KEY, refresh)
  emit('refreshed', {})
  return true
}

export function storeUser(user) {
  write(USER_KEY, user ? JSON.stringify(user) : null)
}

/**
 * End the session. `reason` is 'logout' (the customer asked) or 'expired'
 * (the server refused the refresh token). `forGeneration`, when given, is the
 * session the failure belongs to; a failure from an older session ends nothing.
 */
export function endSession(reason = 'logout', forGeneration) {
  if (forGeneration !== undefined && forGeneration !== generation) return false
  generation += 1
  write(ACCESS_KEY, null)
  write(REFRESH_KEY, null)
  write(USER_KEY, null)
  emit('end', { reason })
  return true
}

/** Another tab changed the session: move this tab's generation on so its in-flight work is dropped. */
export function adoptExternalChange() {
  generation += 1
  emit('external', {})
}

export function subscribe(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

/** Seconds until the JWT expires (negative once it has), or null if it cannot be read. */
export function secondsUntilExpiry(token, now = Date.now()) {
  if (!token || typeof token !== 'string') return null
  const part = token.split('.')[1]
  if (!part) return null
  try {
    const json = JSON.parse(decodeBase64Url(part))
    return typeof json.exp === 'number' ? json.exp - now / 1000 : null
  } catch {
    return null
  }
}

function decodeBase64Url(s) {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(s.length / 4) * 4, '=')
  if (typeof atob === 'function') return atob(b64)
  return Buffer.from(b64, 'base64').toString('binary')
}

export const SESSION_KEYS = { ACCESS_KEY, REFRESH_KEY, USER_KEY }
